// OS CAMPOS DA CLASSIFICAÇÃO DE RUBRICA — uma cópia só (#270, ADR-0015).
//
// Duas telas classificam: o modal do Motor de Rubricas (que a fila da trava
// também abre) e o cartão da conciliação. Até a #270 cada uma tinha a sua
// versão, e elas divergiam — o cartão deixava cadastrar consignatária no
// crédito, o modal não; os rótulos e as dicas eram outros. Regra nova escrita
// duas vezes viraria duas regras. Aqui moram o estado, as regras de tela e os
// campos; quem hospeda fica com o que é dele (descrição e vigência no modal,
// cabeçalho e avisos no cartão).
//
// A REGRA não mora aqui: é `validarClassificacao`, do domínio, a mesma função
// que a API usa para recusar. A tela só a consulta para travar o Salvar e dizer
// o que falta — se as duas divergissem, o operador veria um botão habilitado
// que termina em 400.
//
// O que a tela decide sozinha (e a API não precisa saber):
//   - escolher um tipo do outro grupo troca a categoria entre crédito e
//     facultativa, com aviso — o tipo diz o que a verba é;
//   - no crédito, o tipo pré-preenche o produto, mas SÓ se não havia produto
//     vigente ou declarado e o operador não mexeu nele: um vínculo antigo em
//     CARTAO_CONSIGNADO não troca de balde por efeito colateral;
//   - na facultativa o produto some: o destino é da regra de margem do órgão.

import { useState } from "react";
import { SelectField, TextField, FormGrid, type SelectFieldOption } from "@atlas/ui/web";
import {
  GRUPOS_TIPO_SERVICO,
  categoriaDaNatureza,
  categoriaDoTipo,
  ehCategoriaComCredor,
  produtoPadraoDoTipo,
  tipoSugeridoPeloProduto,
  validarClassificacao,
  type CategoriaLegal,
  type ErrosDaClassificacao,
} from "@atlas/domain";
import type {
  AdminCategoriaLegal,
  AdminRubrica,
  AdminRubricaBucket,
  AdminRubricaVinculo,
  ComportamentoConsignacao,
  NaturezaJuridicaCredor,
  TipoCredor,
} from "@atlas/sdk";
import { opcoesNaturezaJuridica } from "../../lib/natureza-juridica";
import { CATEGORIA_LABEL, PRODUTO_LABEL } from "./motor-rubricas-labels";

/** Valor do select que abre o cadastro de consignatária em vez de escolher uma. */
const NOVO_CREDOR = "novo";

const OPCOES_NATUREZA_JURIDICA = opcoesNaturezaJuridica("— escolha a natureza jurídica —");

/** A ordem do select: o que mais se classifica primeiro. */
const CATEGORIAS_ESCOLHIVEIS: readonly AdminCategoriaLegal[] = [
  "consignacao_credito",
  "consignacao_facultativa",
  "provento",
  "desconto_obrigatorio",
  "desconto_nao_consignado",
];

const OPCOES_TIPO: SelectFieldOption[] = GRUPOS_TIPO_SERVICO.flatMap((g) =>
  g.tipos.map((t) => ({ value: t.codigo, label: t.rotulo, group: g.rotulo })),
);

export interface CredorEscolhivel {
  id: number;
  nome: string;
  tipo?: TipoCredor;
  tenantDeTeste?: boolean;
}

/** A classificação de partida. Strings vazias são "não escolhido". */
export interface ClassificacaoInicial {
  categoria: AdminCategoriaLegal | "";
  tipoServico: string;
  produto: AdminRubricaBucket | "";
  consignatariaId: string;
  credorFinalId: string;
  /** Nome que o relatório de conciliação declarou, para o cadastro na hora. */
  credorNome: string;
  /** Só provento pode ficar fora da base da margem (#273). Sem vínculo, `true`. */
  compoeBaseMargem: boolean;
}

/**
 * De onde a classificação parte. O VÍNCULO ATIVO manda: é ele que sabe
 * `desconto_obrigatorio`, tipo e credor final — a `AdminRubrica` só conhece a
 * natureza antiga, e reabrir por ela rebaixaria um desconto obrigatório a "não
 * consignado". Sem vínculo, a natureza da rubrica; não classificada abre VAZIA
 * (a sugestão do arquivo vira dica, #176). Rubrica nova nasce em crédito.
 *
 * Legado sem tipo (todo vínculo anterior à #270): o tipo vem sugerido quando o
 * produto o determina — os dois cartões. Empréstimo tem cinco tipos; ali o
 * operador escolhe.
 */
export function classificacaoVigente(
  rubrica: AdminRubrica | null,
  vinculo: AdminRubricaVinculo | null,
): ClassificacaoInicial {
  const categoria: AdminCategoriaLegal | "" = vinculo
    ? vinculo.categoria
    : rubrica === null
      ? "consignacao_credito"
      : rubrica.natureza === "nao_classificada"
        ? ""
        : categoriaDaNatureza(rubrica.natureza);
  const produto = (vinculo ? vinculo.produto : rubrica?.tipoMargem) ?? null;
  const consignatariaId = vinculo ? vinculo.consignatariaId : (rubrica?.credorId ?? null);
  const tipo =
    vinculo?.tipoServico ?? (categoria === "consignacao_credito" ? tipoSugeridoPeloProduto(produto) : null);
  return {
    categoria,
    tipoServico: tipo ?? "",
    produto: produto ?? "",
    consignatariaId: consignatariaId != null ? String(consignatariaId) : "",
    credorFinalId: vinculo?.credorFinalId != null ? String(vinculo.credorFinalId) : "",
    credorNome: rubrica?.consignataria ?? "",
    compoeBaseMargem: vinculo?.compoeBaseMargem ?? true,
  };
}

/** A vigência ATIVA de cada rubrica, por código canônico. */
export function vinculosAtivosPorCodigo(vinculos: readonly AdminRubricaVinculo[]): Map<string, AdminRubricaVinculo> {
  const m = new Map<string, AdminRubricaVinculo>();
  for (const v of vinculos) if (v.situacao === "ativo") m.set(v.codigoVerba, v);
  return m;
}

/** O que uma linha do catálogo É, para as listagens: o vínculo ativo manda; sem ele, a natureza da rubrica. */
export interface ClasseDaRubrica {
  categoria: AdminCategoriaLegal;
  tipoServico: string | null;
  produto: AdminRubricaBucket | null;
  consignatariaId: number | null;
  credorFinalId: number | null;
}

export function classeDaRubrica(r: AdminRubrica, ativo: AdminRubricaVinculo | undefined): ClasseDaRubrica {
  if (ativo) {
    return {
      categoria: ativo.categoria,
      tipoServico: ativo.tipoServico,
      produto: ativo.produto,
      consignatariaId: ativo.consignatariaId,
      credorFinalId: ativo.credorFinalId,
    };
  }
  return {
    categoria: categoriaDaNatureza(r.natureza),
    tipoServico: null,
    produto: r.tipoMargem,
    consignatariaId: r.credorId,
    credorFinalId: null,
  };
}

/** Consignatária que ainda vai nascer de `credorNovo`: presente para a regra do credor final. */
const CONSIGNATARIA_A_CRIAR = -1;

export function useCamposClassificacao(inicial: ClassificacaoInicial, comportamentoDeclarado?: ComportamentoConsignacao) {
  const [categoria, setCategoriaCru] = useState<AdminCategoriaLegal | "">(inicial.categoria);
  const [tipoServico, setTipoCru] = useState(inicial.tipoServico);
  const [produto, setProdutoCru] = useState<AdminRubricaBucket | "">(inicial.produto);
  const [produtoTocado, setProdutoTocado] = useState(false);
  const [consignatariaId, setConsignatariaId] = useState(inicial.consignatariaId);
  const [criandoCredor, setCriandoCredor] = useState(false);
  const [credorNome, setCredorNome] = useState(inicial.credorNome);
  const [credorCnpj, setCredorCnpj] = useState("");
  // Consignatária nova nasce com natureza jurídica (#269): a API recusa sem ela.
  const [credorNatureza, setCredorNatureza] = useState<NaturezaJuridicaCredor | "">("");
  const [credorFinalId, setCredorFinalId] = useState(inicial.credorFinalId);
  // Rubrica com comportamento declarado abre com ele: escolher de novo às cegas
  // mudaria o comportamento sem querer.
  const [comportamentoEscolhido, setComportamento] = useState<ComportamentoConsignacao | "">("");
  const comportamento = comportamentoEscolhido || comportamentoDeclarado || "";
  const [aviso, setAviso] = useState<string | null>(null);
  const [compoeBaseMargem, setCompoeBaseMargem] = useState(inicial.compoeBaseMargem);

  const credito = categoria === "consignacao_credito";
  const facultativa = categoria === "consignacao_facultativa";
  const temCredor = categoria !== "" && ehCategoriaComCredor(categoria as CategoriaLegal);

  function setCategoria(c: AdminCategoriaLegal | "") {
    setAviso(null);
    setCategoriaCru(c);
    if (c !== "consignacao_credito") setProdutoCru("");
    // Só provento fica fora da base: fora dele o flag volta ao padrão.
    if (c !== "provento") setCompoeBaseMargem(true);
    if (c === "" || !ehCategoriaComCredor(c)) {
      setTipoCru("");
      setConsignatariaId("");
      setCriandoCredor(false);
      setCredorFinalId("");
    } else if (tipoServico !== "" && categoriaDoTipo(tipoServico) !== c) {
      // O tipo escolhido é do outro grupo: não vale mais.
      setTipoCru("");
    }
  }

  function setTipoServico(t: string) {
    setTipoCru(t);
    if (t === "") return;
    const doTipo = categoriaDoTipo(t);
    let categoriaFinal = categoria;
    if (doTipo && doTipo !== categoria) {
      // O tipo diz o que a verba é: escolher "Plano de saúde" numa verba de
      // crédito a torna facultativa. A API recusaria a combinação.
      categoriaFinal = doTipo;
      setCategoriaCru(doTipo);
      if (doTipo !== "consignacao_credito") setProdutoCru("");
      setAviso(`A categoria passou a ${CATEGORIA_LABEL[doTipo]}: é a categoria deste tipo de serviço.`);
    } else {
      setAviso(null);
    }
    if (categoriaFinal === "consignacao_credito" && !produtoTocado && inicial.produto === "") {
      setProdutoCru(produtoPadraoDoTipo(t) ?? "");
    }
  }

  function setProduto(p: AdminRubricaBucket | "") {
    setProdutoTocado(true);
    setProdutoCru(p);
  }

  function escolherConsignataria(valor: string) {
    setCriandoCredor(valor === NOVO_CREDOR);
    setConsignatariaId(valor === NOVO_CREDOR ? "" : valor);
  }

  const consignatariaNum = criandoCredor ? CONSIGNATARIA_A_CRIAR : consignatariaId ? Number(consignatariaId) : null;
  const erros: ErrosDaClassificacao & { credorNovo?: string; comportamento?: string } = {
    ...validarClassificacao(
      {
        categoria: (categoria || "nao_classificada") as CategoriaLegal,
        tipoServico: temCredor && tipoServico ? tipoServico : null,
        produto: credito && produto ? produto : null,
        consignatariaId: temCredor ? consignatariaNum : null,
        credorFinalId: temCredor && credorFinalId ? Number(credorFinalId) : null,
        compoeBaseMargem,
      },
      { exigeTipo: true },
    ),
    ...(temCredor && criandoCredor && (credorNome.trim() === "" || credorNatureza === "")
      ? { credorNovo: "informe o nome e a natureza jurídica da consignatária" }
      : {}),
    ...(facultativa && comportamento === "" ? { comportamento: "escolha o comportamento" } : {}),
  };
  const valida = Object.keys(erros).length === 0;

  /** Mudou alguma coisa em relação ao que abriu? Só a descrição não reclassifica. */
  const mudou =
    categoria !== inicial.categoria ||
    tipoServico !== inicial.tipoServico ||
    produto !== inicial.produto ||
    consignatariaId !== inicial.consignatariaId ||
    credorFinalId !== inicial.credorFinalId ||
    compoeBaseMargem !== inicial.compoeBaseMargem ||
    (temCredor && criandoCredor) ||
    (facultativa && comportamento !== (comportamentoDeclarado ?? ""));

  /** O corpo novo do `rubricas.confirmar` (#270), sem `natureza`. */
  function payload(bancos: readonly CredorEscolhivel[]) {
    const cadastra = temCredor && criandoCredor && credorNome.trim() !== "" && credorNatureza !== "";
    const consignataria = temCredor && !criandoCredor && consignatariaId ? Number(consignatariaId) : null;
    return {
      categoria: categoria as AdminCategoriaLegal,
      tipoServico: temCredor ? tipoServico || null : null,
      produto: credito && produto ? produto : null,
      consignatariaId: consignataria,
      ...(cadastra
        ? {
            credorNovo: {
              nome: credorNome.trim(),
              // `\D` (não-dígito): a máscara não vai para o servidor.
              cnpj: credorCnpj.replace(/\D/g, "") || undefined,
              naturezaJuridica: credorNatureza as NaturezaJuridicaCredor,
            },
          }
        : {}),
      consignataria: consignataria !== null ? (bancos.find((b) => b.id === consignataria)?.nome ?? null) : null,
      credorFinalId: temCredor && credorFinalId ? Number(credorFinalId) : null,
      ...(facultativa && comportamento ? { comportamento } : {}),
      // Só provento manda o flag (#273): nas outras categorias ele não existe.
      ...(categoria === "provento" ? { compoeBaseMargem } : {}),
    };
  }

  return {
    categoria,
    tipoServico,
    produto,
    consignatariaId,
    criandoCredor,
    credorNome,
    credorCnpj,
    credorNatureza,
    credorFinalId,
    comportamento,
    compoeBaseMargem,
    aviso,
    credito,
    facultativa,
    temCredor,
    cadastraCredor: temCredor && criandoCredor,
    erros,
    valida,
    mudou,
    setCategoria,
    setTipoServico,
    setProduto,
    escolherConsignataria,
    setCredorNome,
    setCredorCnpj,
    setCredorNatureza,
    setCredorFinalId,
    setComportamento,
    setCompoeBaseMargem,
    payload,
  };
}

export type CamposClassificacaoEstado = ReturnType<typeof useCamposClassificacao>;

function rotuloDoCredor(b: CredorEscolhivel): string {
  return `${b.nome}${b.tenantDeTeste ? " (teste)" : ""}`;
}

/**
 * Os campos, controlados pelo hook. `sugestao` é o rótulo da categoria que o
 * arquivo sugere, mostrado como dica enquanto nada foi escolhido.
 */
export function CamposClassificacao({
  c,
  bancos,
  sugestao,
}: {
  c: CamposClassificacaoEstado;
  bancos: readonly CredorEscolhivel[];
  sugestao?: string | null;
}) {
  const consignatarias = bancos.filter((b) => b.tipo === "consignataria" && String(b.id) !== c.consignatariaId);
  return (
    <div style={{ display: "grid", gap: 12 }}>
      <SelectField
        label="Categoria legal"
        value={c.categoria}
        onChange={(e) => c.setCategoria(e.target.value as AdminCategoriaLegal | "")}
        options={[
          ...(c.categoria === "" ? [{ value: "", label: "— escolha a categoria —" }] : []),
          ...CATEGORIAS_ESCOLHIVEIS.map((k) => ({ value: k, label: CATEGORIA_LABEL[k] })),
        ]}
        hint={
          c.categoria === ""
            ? `Ainda não classificada${sugestao ? ` — sugestão do arquivo: ${sugestao}` : ""}. Escolha para salvar.`
            : c.credito
              ? "Gera dívida e ocupa a margem do produto."
              : c.facultativa
                ? "Plano de saúde, odonto, seguro, mensalidade: entra no movimento pela consignatária, sem contrato. O que ocupa de margem é a regra do órgão que diz."
                : c.categoria === "desconto_obrigatorio"
                  ? "Desconto que a lei impõe (previdência, IR, pensão judicial, restituição ao erário): reduz a base da margem e não é consignação."
                  : c.categoria === "provento"
                    ? "Remuneração do servidor. Compõe a base da margem, salvo vantagem transitória (13º, férias, hora extra)."
                    : "Não gera dívida: o desconto é ignorado pelo motor."
        }
      />
      {c.aviso ? <div style={{ fontSize: 12, color: "var(--gold-500)" }}>{c.aviso}</div> : null}

      {c.categoria === "provento" ? (
        <label
          style={{ display: "flex", gap: 10, alignItems: "flex-start", fontSize: 13, color: "var(--text-muted)", cursor: "pointer" }}
        >
          <input
            type="checkbox"
            checked={c.compoeBaseMargem}
            onChange={(e) => c.setCompoeBaseMargem(e.target.checked)}
            style={{ marginTop: 3 }}
          />
          <span>
            <b style={{ color: "var(--text)" }}>Compõe a base da margem</b>
            <br />
            Desmarque para vantagem transitória — 13º, férias, hora extra, diárias. A lei a exclui da base líquida
            legal, e a margem calculada sobre ela não a conta.
          </span>
        </label>
      ) : null}

      {c.temCredor ? (
        <SelectField
          label="Tipo de serviço"
          value={c.tipoServico}
          onChange={(e) => c.setTipoServico(e.target.value)}
          options={[{ value: "", label: "— escolha o tipo de serviço —" }, ...OPCOES_TIPO]}
          required
          hint={c.erros.tipoServico && c.tipoServico !== "" ? c.erros.tipoServico : "Do catálogo da Atlas. É ele que a regra de margem do órgão mapeia."}
        />
      ) : null}

      {c.credito ? (
        <SelectField
          label="Produto"
          value={c.produto}
          onChange={(e) => c.setProduto(e.target.value as AdminRubricaBucket | "")}
          options={[
            { value: "", label: "Sem produto (não vira dívida)" },
            ...(Object.keys(PRODUTO_LABEL) as AdminRubricaBucket[]).map((b) => ({ value: b, label: PRODUTO_LABEL[b] })),
          ]}
          hint={
            c.produto
              ? "Decide qual margem o desconto ocupa. O percentual (ou o saldo informado) é da regra do órgão, não do produto."
              : "Sem produto a verba não vira dívida: o desconto vai para conciliação."
          }
        />
      ) : null}

      {c.temCredor ? (
        <>
          <SelectField
            label="Consignatária"
            value={c.criandoCredor ? NOVO_CREDOR : c.consignatariaId}
            onChange={(e) => c.escolherConsignataria(e.target.value)}
            options={[
              { value: "", label: c.credito ? "— credor a identificar —" : "— escolha a consignatária —" },
              ...bancos.map((b) => ({ value: String(b.id), label: rotuloDoCredor(b) })),
              { value: NOVO_CREDOR, label: "+ cadastrar consignatária…" },
            ]}
            hint={
              c.consignatariaId || c.criandoCredor
                ? "Quem recebe o repasse e responde pelo desconto no movimento."
                : c.credito
                  ? "Sem ela, a dívida nasce com credor a identificar: consome margem, e o convênio liga a consignatária quando assinar."
                  : "Sem ela, o desconto entra no movimento sem dono."
            }
          />
          {c.criandoCredor ? (
            <FormGrid cols={2}>
              <TextField
                label="Nome da consignatária"
                value={c.credorNome}
                onChange={(e) => c.setCredorNome(e.target.value)}
                placeholder="Ex.: ASSALCE"
                required
              />
              <TextField
                label="CNPJ (opcional, mas evita duplicar)"
                value={c.credorCnpj}
                onChange={(e) => c.setCredorCnpj(e.target.value)}
                placeholder="00.000.000/0000-00"
                hint="CNPJ igual reaproveita o registro em vez de criar um segundo."
              />
              <SelectField
                label="Natureza jurídica"
                value={c.credorNatureza}
                onChange={(e) => c.setCredorNatureza(e.target.value as NaturezaJuridicaCredor | "")}
                options={OPCOES_NATUREZA_JURIDICA}
                required
                hint={c.credorNatureza ? undefined : "Obrigatória para cadastrar."}
              />
            </FormGrid>
          ) : null}
          <SelectField
            label="Credor final (opcional)"
            value={c.credorFinalId}
            onChange={(e) => c.setCredorFinalId(e.target.value)}
            options={[
              { value: "", label: "— o próprio repasse —" },
              ...consignatarias.map((b) => ({ value: String(b.id), label: rotuloDoCredor(b) })),
            ]}
            hint={c.erros.credorFinalId ?? "Quem presta o serviço, quando não é quem recebe o repasse (Unimed, via ASSALCE)."}
          />
        </>
      ) : null}

      {c.facultativa ? (
        <SelectField
          label="Comportamento"
          value={c.comportamento}
          onChange={(e) => c.setComportamento(e.target.value as ComportamentoConsignacao | "")}
          options={[
            ...(c.comportamento === "" ? [{ value: "", label: "— escolha o comportamento —" }] : []),
            { value: "recorrente", label: "Recorrente — repete todo mês até ser excluída" },
            { value: "prazo", label: "Com prazo — repete até a competência final" },
            { value: "avulsa", label: "Avulsa — vale só no mês em que foi informada" },
          ]}
          hint="Decide se a Atlas repete o desconto no movimento do mês seguinte quando não chega arquivo novo."
        />
      ) : null}
    </div>
  );
}
